import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

interface DevicePWAConfig {
  id: string;
  device_id: string;
  organization_id: string;
  home_widgets: Record<string, boolean>;
  home_layout_order: string[];
  logo_light_url: string | null;
  logo_dark_url: string | null;
  welcome_message: string | null;
  shop_template: string;
  shop_settings: Record<string, any>;
  visible_categories: string[];
  category_order: string[];
  featured_product_ids: string[];
  contact_phone: string | null;
  contact_email: string | null;
  contact_address: string | null;
  contact_phone_enabled: boolean;
  contact_email_enabled: boolean;
  opening_hours: Record<string, any> | null;
  opening_hours_enabled: boolean;
  auth_method: string | null;
  guest_shopping_enabled: boolean;
  created_at: string;
  updated_at: string;
}

const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

// Config changes are rare; rely on focus/mount refetches + a short staleTime
// instead of a persistent realtime channel per active PWA session.
const CONFIG_STALE_TIME = 30 * 1000;
const CONFIG_GC_TIME = 10 * 60 * 1000;

export const useDevicePWAConfig = (deviceId?: string) => {
  const queryClient = useQueryClient();

  const { data: config, isLoading, error } = useQuery({
    queryKey: ["device-pwa-config", deviceId],
    queryFn: async () => {
      if (!deviceId || !isUuid(deviceId)) return null;

      console.log('[useDevicePWAConfig] 🔍 Fetching config via RPC for device:', deviceId);

      // Single RPC call - all merge logic happens server-side
      const { data, error } = await supabase.rpc('get_device_pwa_config', {
        p_device_id: deviceId
      });

      if (error) {
        console.error('[useDevicePWAConfig] ❌ RPC error:', error);
        throw error;
      }

      if (!data) {
        console.log('[useDevicePWAConfig] ⚠️ No config found for device');
        return null;
      }

      // Process home_layout_order to only include enabled widgets
      const homeWidgets = (data.home_widgets || {}) as Record<string, boolean>;
      const rawLayoutOrder = (data.home_layout_order || []) as string[];
      
      // Get enabled widget keys
      const enabledWidgets = Object.entries(homeWidgets)
        .filter(([_, enabled]) => enabled === true)
        .map(([key]) => key);

      // If no layout order saved, use enabled widgets as order
      const layoutOrder = rawLayoutOrder.length > 0 
        ? rawLayoutOrder.filter(key => enabledWidgets.includes(key))
        : enabledWidgets;

      const result = {
        ...data,
        home_widgets: homeWidgets,
        home_layout_order: layoutOrder,
      } as DevicePWAConfig;

      console.log('[useDevicePWAConfig] ✅ Config loaded:', {
        deviceId,
        enabledWidgets,
        layoutOrder,
        orgId: data.organization_id,
      });

      return result;
    },
    enabled: !!deviceId && isUuid(deviceId),
    staleTime: CONFIG_STALE_TIME,
    gcTime: CONFIG_GC_TIME,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const updateConfig = useMutation({
    mutationFn: async (updates: Partial<DevicePWAConfig>) => {
      if (!deviceId || !isUuid(deviceId)) throw new Error("Ogiltigt enhets-ID");

      const { data: deviceData, error: deviceError } = await supabase
        .from("ziplink_devices")
        .select("tenant_id")
        .eq("id", deviceId)
        .single();

      if (deviceError || !deviceData) {
        console.error("Device error:", deviceError);
        throw new Error("Device not found");
      }

      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("id")
        .eq("tenant_id", deviceData.tenant_id)
        .single();

      if (orgError || !orgData) {
        console.error("Organization error:", orgError);
        throw new Error("Organization not found");
      }

      const orgId = orgData.id;

      const { data: existing } = await supabase
        .from("device_pwa_config")
        .select("id")
        .eq("device_id", deviceId)
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from("device_pwa_config")
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq("device_id", deviceId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from("device_pwa_config")
          .insert({
            device_id: deviceId,
            organization_id: orgId,
            ...updates,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-pwa-config", deviceId] });
      toast.success("Konfigurationen har sparats");
    },
    onError: (error: any) => {
      console.error("Error updating device PWA config:", error);
      toast.error("Kunde inte spara konfigurationen");
    },
  });

  return {
    config: config || null, // Return null when loading - no default config!
    isLoading,
    error,
    updateConfig: updateConfig.mutate,
    isUpdating: updateConfig.isPending,
  };
};
